import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { AuthStateFacade, WatchlistFacade } from '@zero/shared/util-global-state';
import {
  BackendResponseStatus,
  BuyingPowerDetails,
  Customer,
  Customers,
  EnrolledDevice,
  IBackendResponse,
  MoneyTransaction,
  MoneyTransactionsDataHolder,
  OrderDataHolder,
  OrderStatus,
  PositionData,
  PositionDataHolder,
  Post,
  PushMessage,
  PushMessageId,
  PushMessageType,
  ServerPushMessage,
  ServerPushMessageType,
  UnreadDocuments,
} from '@zero/shared/util-models';
import { logoutNotificationThreshold } from 'constants/shared-constants';
import { RequestStream, Responder, Session } from 'diffusion';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription, firstValueFrom, interval, of } from 'rxjs';
import { catchError, debounceTime, filter, map } from 'rxjs/operators';
import { AuthService } from '../authentication/auth.service';
import { DiffusionSessionService } from '../diffusion/diffusion-session.service';
import { HttpResponseHandleHelper } from '../http/http-response-handle-helper';
@Injectable({
  providedIn: 'root',
})
export class DataService {
  readonly #watchlistFacade = inject(WatchlistFacade);
  readonly #authService = inject(AuthService);
  readonly #http = inject(HttpClient);
  readonly #diffusionSessionService = inject(DiffusionSessionService);
  readonly #ngzone = inject(NgZone);
  readonly #authStateFacade = inject(AuthStateFacade);

  private readonly diffusionRequestStreamPath = 'message';
  private readonly diffusionPushResponse = JSON.stringify({});

  /**
   * Push message notifications
   */
  private readonly pushMsgTypeMap = new Map([
    [ServerPushMessageType.TEXT, PushMessageType.msg],
    [ServerPushMessageType.ORDER, PushMessageType.order],
    [ServerPushMessageType.TICKET, PushMessageType.ticket],
    [ServerPushMessageType.TSLUPDATE, PushMessageType.tslupdate],
  ]);
  private readonly storedPushMsgIdsKey = 'pushmessage_ids';
  private readonly maxStoredPushMsgIds = 1000;

  private readonly removeOldPushMsgIdsTrigger$ = new Subject<void>();
  private readonly loadPushMessagesTrigger$ = new Subject<void>();

  public selectedCustomerChanged = new ReplaySubject<string>(1);
  public logOutNotificationSent = false;

  public diffusionSessionSubscription: Subscription;
  public msgSubject: Subject<PushMessage> = new Subject<PushMessage>();

  /**
   * Posteingang
   */
  public documents = new Map<string, Post[]>(); // maps Posteingaenge by 'm', 'p', 'a'
  public unreadDocuments: UnreadDocuments = { eingang: 0, mifid: 0 };
  private unreadDocumentsReloadTrigger$ = new Subject<void>();
  public unreadDocumentsCountChanged$ = new Subject<void>();
  private customerData: Customers = {};
  private selectedCustomer: string;
  private readonly initDone: Promise<boolean>;
  private initDoneFn: (value: boolean | PromiseLike<boolean>) => void;
  private readonly MAX_DOCS_PER_PE_REQUEST = 1000;

  /**
   * Tickets
   */
  public unreadTickets = 0;
  private unreadTicketsReloadTrigger$ = new Subject<void>();
  public unreadTicketsCountChanged$ = new Subject<void>();

  /**
   * BP Details
   */
  public bpDetails: BuyingPowerDetails;
  private bpDetails$ = new ReplaySubject<BuyingPowerDetails>(1);

  /**
   * Money Transactions
   */
  public moneyTransactions: MoneyTransaction[] = [];
  public hasMoreMoneyTransactions = false;
  private moneyTransactions$ = new ReplaySubject<MoneyTransactionsDataHolder>(1);
  private moneyTransactionsReload$ = new Subject<any>();

  /**
   * Possitions
   */
  private positions$ = new BehaviorSubject<IBackendResponse<PositionDataHolder> | null>(null);

  private clientUpdateAvailable = false;
  private clientVersion: string;
  private periodicallyReloadSubscription = new Subscription();

  private adAgreeNotePostponed: Date;

  private enrolledDevices$ = new BehaviorSubject<EnrolledDevice[]>(null);
  private enrolledDevicesReload$ = new Subject<void>();

  constructor() {
    this.initDone = new Promise<boolean>((res) => {
      this.initDoneFn = res;
    });

    this.#authService.isLoggedIn$().subscribe((loggedIn) => {
      this.#authStateFacade.setLoggedIn(loggedIn);
      if (loggedIn === true) {
        this.logOutNotificationSent = false;
        this.customerData = this.#authService.getCurrentCustomerData();
        if (this.selectedCustomer) {
          this.#authStateFacade.setCustomerId(this.selectedCustomer);
          // eslint-disable-next-line no-prototype-builtins
          if (!this.customerData.hasOwnProperty(this.selectedCustomer)) {
            this.setOwnerAsSelectedCustomer();
          }
        } else {
          this.setOwnerAsSelectedCustomer();
        }

        // Subscribe from Diffusion session updates
        this.diffusionSessionSubscription = this.#diffusionSessionService.diffusionSession$.subscribe((diffusionSession) => {
          if (diffusionSession) {
            this.registerMessageHandler(diffusionSession);
            this.triggerLoadPushMessages();
          }
        });

        // load watchlist
        this.#watchlistFacade.loadWatchlist(true);
      } else {
        this.resetAllData();
        if (this.diffusionSessionSubscription && !this.diffusionSessionSubscription.closed) {
          this.diffusionSessionSubscription.unsubscribe();
        }
      }

      this.initDoneFn(true);
    });
    this.#authService.isCustomer$().subscribe((isCustomer) => {
      if (isCustomer === true) {
        if (this.periodicallyReloadSubscription.closed) {
          this.periodicallyReloadSubscription = new Subscription();
        }
        this.periodicallyReloadSubscription.add(
          interval(300000).subscribe(() => {
            this.triggerReloadUnreadDocuments();
          }),
        );
        this.periodicallyReloadSubscription.add(
          interval(300000).subscribe(() => {
            this.triggerReloadUnreadTickets();
          }),
        );
        this.triggerReloadUnreadDocuments();
        this.triggerReloadUnreadTickets();
        this.triggerRemoveOldPushMsgIds();
        this.triggerLoadPushMessages();
      } else {
        this.periodicallyReloadSubscription.unsubscribe();
      }
    });

    this.#authService.getRemainingTimeToForcedLogout$().subscribe((timestamp: number) => {
      this.sendLogoutNotification(timestamp);
    });

    this.#authService.clearPushNotifications$
      .pipe(filter((shouldClearPushMessages) => !!shouldClearPushMessages))
      .subscribe(() => this.msgSubject.next(null));

    this.initSubscriptions();
    this.initCheckForNewClientVersion();
  }

  private initSubscriptions(): void {
    // money transactions
    this.moneyTransactionsReload$.pipe(debounceTime(200)).subscribe((value: { size: number; direction: 'asc' | 'desc' }) => {
      this.reloadMoneyTransactions(value.size, value.direction);
    });

    this.unreadDocumentsReloadTrigger$.pipe(debounceTime(500)).subscribe(() => this.reloadUnreadDocumentsCount());

    this.unreadTicketsReloadTrigger$.pipe(debounceTime(500)).subscribe(() => this.reloadUnreadTicketsCount());

    this.enrolledDevicesReload$.pipe(debounceTime(200)).subscribe(() => {
      this.reloadEnrolledDevices();
    });

    // push notification messages
    this.removeOldPushMsgIdsTrigger$.pipe(debounceTime(200)).subscribe(() => {
      this.removeOldPushMsgIds();
    });
    this.loadPushMessagesTrigger$.pipe(debounceTime(200)).subscribe(() => {
      this.loadPushMessages();
    });
  }

  public loadPositions$() {
    this.loadPositionsHttp();
  }

  public getPositions$(): Observable<IBackendResponse<PositionDataHolder>> {
    return this.positions$.asObservable();
  }

  public loadPositions(): Promise<PositionDataHolder> {
    return this.loadPositionsHttp();
  }

  public loadPositionsHttp(): Promise<PositionDataHolder> {
    const httpClient: HttpClient = this.#http;
    return new Promise<PositionDataHolder>((resolve, reject) => {
      if (!this.getSelectedCustomer()) {
        resolve(undefined);
        return;
      }
      this.initDone.then(() => {
        const positionsUrl = `${environment.apihost}/api/trading/positions?customerId=${this.getSelectedCustomer()}`;
        firstValueFrom(httpClient.get(positionsUrl)).then(
          (res) => {
            // Success
            const positionsDataHolder: PositionDataHolder = {
              list: [],
              instruments: [],
            };
            Object.assign(positionsDataHolder, res);
            for (let i = 0; i < positionsDataHolder.list.length; i++) {
              positionsDataHolder.list[i] = Object.assign(new PositionData(), positionsDataHolder.list[i]);
            }

            this.positions$.next({
              status: BackendResponseStatus.SUCCESS,
              data: positionsDataHolder,
            } as IBackendResponse<PositionDataHolder>);
            resolve(positionsDataHolder);
          },
          (msg) => {
            // Error
            reject(msg);
          },
        );
      });
    });
  }

  public loadOrdersHttp(status: OrderStatus, pageSize?: number, sort?: string, direction?: 'asc' | 'desc'): Promise<OrderDataHolder> {
    const httpClient: HttpClient = this.#http;
    return new Promise<OrderDataHolder>((resolve, reject) => {
      this.initDone.then(() => {
        let ordersUrl = `${environment.apihost}/api/trading/orders?customerId=${this.getSelectedCustomer()}&status=${status}`;
        if (pageSize) {
          ordersUrl += `&page=0&size=${pageSize}`;
        }
        if (sort) {
          ordersUrl += `&sort=${sort}`;
          if (direction) {
            ordersUrl += `,${direction}`;
          }
        }
        firstValueFrom(httpClient.get(ordersUrl))
          .then(
            (res) => {
              // Success
              const orderDataHolder: OrderDataHolder = {
                list: [],
                hasNext: false,
              };
              Object.assign(orderDataHolder, res);

              resolve(orderDataHolder);
            },
            (msg) => {
              // Error
              reject(msg);
            },
          )
          .catch(() => {
            // TODO: error handline
          });
      });
    });
  }

  public getSelectedCustomer(): string {
    return this.selectedCustomer;
  }

  public getSelectedCustomerItem(): Customer {
    return this.customerData[this.selectedCustomer];
  }

  private triggerRemoveOldPushMsgIds(): void {
    this.removeOldPushMsgIdsTrigger$.next();
  }

  private removeOldPushMsgIds(): void {
    const storedPushMsgIds = this.getStoredPushMsgIds();
    const currentPushMsgIds = storedPushMsgIds.filter((pushMsgId) => this.isFromToday(pushMsgId));
    this.storePushMsgIds(currentPushMsgIds);
  }

  private triggerLoadPushMessages(): void {
    this.loadPushMessagesTrigger$.next();
  }

  private loadPushMessages(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thiz = this;
    this.initDone.then(() => {
      if (!this.getSelectedCustomer()) {
        return;
      }
      const url = `${environment.apihost + environment.notificationServiceUrl}/pushmessages/${this.getSelectedCustomer()}`;
      this.#http
        .get<ServerPushMessage[]>(url)
        .pipe(catchError(() => of([])))
        .subscribe((serverPushMsgs) => {
          serverPushMsgs.forEach((serverPushMsg) => thiz.processLoadedPushMsg(serverPushMsg));
        });
    });
  }

  private registerMessageHandler(session: Session): void {
    this.initDone.then(() => {
      if (!this.customerData || !this.selectedCustomer) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const thiz = this;
      import('diffusion').then(() => {
        const requestStream: RequestStream = {
          onRequest: (path: string, request: any, responder: Responder) => {
            const serverPushMsg: ServerPushMessage = JSON.parse(request) as ServerPushMessage;
            thiz.processDiffusionPushMsg(serverPushMsg);
            responder.respond(thiz.diffusionPushResponse);
          },
          onError: () => {
            // TODO: error handling
          },
          onClose: () => {
            // TODO: close handling
          },
        };
        session.messages.setRequestStream(thiz.diffusionRequestStreamPath, requestStream);
      });
    });
  }

  private processLoadedPushMsg(serverPushMsg: ServerPushMessage): void {
    const pushMsg = this.createPushMsg(serverPushMsg);
    const pushMsgId: PushMessageId = {
      id: serverPushMsg.id,
      ts: serverPushMsg.timestamp,
    };
    if (this.isPushMsgToBeStored(serverPushMsg)) {
      const isNew = this.storePushMsgIdIfNew(pushMsgId);
      if (isNew) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const thiz = this;
        this.#ngzone.run(() => {
          thiz.msgSubject.next(pushMsg);
        });
      }
    }
  }

  private processDiffusionPushMsg(serverPushMsg: ServerPushMessage): void {
    const pushMsg = this.createPushMsg(serverPushMsg);
    const pushMsgId: PushMessageId = {
      id: serverPushMsg.id,
      ts: serverPushMsg.timestamp,
    };
    if (this.isPushMsgToBeStored(serverPushMsg)) {
      this.storePushMsgIdIfNew(pushMsgId);
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thiz = this;
    this.#ngzone.run(() => {
      thiz.msgSubject.next(pushMsg);
    });
  }

  private createPushMsg(serverPushMsg: ServerPushMessage): PushMessage {
    let type;
    if (serverPushMsg.type) {
      type = this.pushMsgTypeMap.get(serverPushMsg.type);
    }
    if (typeof type === 'undefined') {
      type = PushMessageType.unknown;
    }

    let parsedData;
    if (serverPushMsg.data) {
      if (typeof serverPushMsg.data === 'string') {
        parsedData = JSON.parse(serverPushMsg.data);
      } else {
        parsedData = serverPushMsg.data;
      }
    }

    return {
      customer: serverPushMsg.customer,
      msg: serverPushMsg.msg,
      msgData: parsedData,
      type,
      clientReceiveDate: new Date(),
      id: serverPushMsg.id,
      createdDate: new Date(serverPushMsg.timestamp),
    };
  }

  private storePushMsgIdIfNew(pushMsgId: PushMessageId): boolean {
    const storedPushMsgIds = this.getStoredPushMsgIds();
    const isNew = !storedPushMsgIds.some((exPushMsgId) => exPushMsgId.id === pushMsgId.id);
    if (isNew) {
      storedPushMsgIds.push(pushMsgId);
      this.storePushMsgIds(storedPushMsgIds);
    }
    return isNew;
  }

  private isFromToday(pushMsgId: PushMessageId): boolean {
    return this.isToday(pushMsgId.ts);
  }

  private isToday(timestamp: number): boolean {
    const date = new Date(timestamp);
    const lastMidnight = new Date();
    lastMidnight.setHours(0, 0, 0, 0);
    return date > lastMidnight;
  }

  private getStoredPushMsgIds(): PushMessageId[] {
    const storedPushMsgIdsJson = localStorage.getItem(this.storedPushMsgIdsKey);
    return storedPushMsgIdsJson ? JSON.parse(storedPushMsgIdsJson) : [];
  }

  private storePushMsgIds(pushMsgIds: PushMessageId[]): void {
    const slicedPushMsgIds = pushMsgIds.slice(Math.max(pushMsgIds.length - this.maxStoredPushMsgIds, 0));
    const pushMsgIdsJson = JSON.stringify(slicedPushMsgIds);
    localStorage.setItem(this.storedPushMsgIdsKey, pushMsgIdsJson);
  }

  public triggerReloadUnreadDocuments(): void {
    this.unreadDocumentsReloadTrigger$.next();
  }

  private reloadUnreadDocumentsCount(): void {
    if (!this.getSelectedCustomer()) {
      return;
    }

    const unreadMifidBeforeReload = this.unreadDocuments.mifid;
    const unreadEingangBeforeReload = this.unreadDocuments.eingang;
    this.#http
      .get<Record<string, number>>(`${environment.apihost + environment.posteingangServiceUrl}/${this.getSelectedCustomer()}/unread`)
      .pipe(catchError(() => of({ M: 0, P: 0 })))
      .subscribe((res) => {
        this.unreadDocuments.mifid = res.M;
        this.unreadDocuments.eingang = res.P;
        if (unreadMifidBeforeReload !== this.unreadDocuments.mifid || unreadEingangBeforeReload !== this.unreadDocuments.eingang) {
          this.unreadDocumentsCountChanged$.next();
        }
      });
  }

  public async loadDocuments(type?: string, forceReload?: boolean): Promise<Post[]> {
    await this.initDone;
    if (!this.getSelectedCustomer()) {
      return null;
    }
    if (!type) {
      type = 'P'; // default
    }

    if (forceReload === true) {
      this.deleteCachedDocuments();
    }

    let params = new HttpParams();
    params = params.append('t', type);
    params = params.append('m', String(this.MAX_DOCS_PER_PE_REQUEST));
    let currentDocs = this.documents.get(type);
    if (!currentDocs) {
      currentDocs = new Array<Post>();
      this.documents.set(type, currentDocs);
    }
    let lastResponse = new Array<Post>();
    do {
      if (currentDocs.length > 0) {
        params = params.set('l', currentDocs[currentDocs.length - 1].id);
      }
      const posteingangUrl = `${
        environment.apihost + environment.posteingangServiceUrl
      }/${this.getSelectedCustomer()}?${params.toString()}`;
      lastResponse = await this.doRequest(posteingangUrl);
      currentDocs = currentDocs.concat(lastResponse);
      this.documents.set(type, currentDocs);
    } while (lastResponse.length === this.MAX_DOCS_PER_PE_REQUEST);
    this.triggerReloadUnreadDocuments();
    return this.documents.get(type);
  }

  private async doRequest(posteingangUrl: string): Promise<Post[]> {
    const data = await firstValueFrom(this.#http.get<any[]>(posteingangUrl));
    const resArr: Post[] = new Array<Post>();
    for (const item of data) {
      const p = new Post();
      Object.assign(p, item);
      resArr.push(p);
    }
    return resArr;
  }

  private setOwnerAsSelectedCustomer(): void {
    // select owner customer
    const owner = Object.entries(this.customerData).find((cust) => cust[1].owner === true);
    this.selectedCustomer = typeof owner !== 'undefined' ? owner[0] : undefined;
    this.selectedCustomerChanged.next(this.selectedCustomer);
    this.#authStateFacade.setCustomerId(this.selectedCustomer);
  }

  private sendLogoutNotification(timestamp: number): void {
    if (timestamp && timestamp < logoutNotificationThreshold && timestamp > 0 && !this.logOutNotificationSent) {
      const msg: PushMessage = {
        msg: `${new Date().toLocaleTimeString()}: Deine Session läuft in Kürze ab.`,
        msgData: 'Du wirst in 5 min automatisch ausgeloggt. Um dies zu umgehen kannst Du Dich auch sofort aus- und wieder einloggen.',
        type: PushMessageType.msg,
        id: crypto.randomUUID(),
        createdDate: new Date(),
      };
      this.msgSubject.next(msg);
      this.logOutNotificationSent = true;
    }
  }

  public loadBPDetails$(isin?: string): Observable<BuyingPowerDetails> {
    this.loadBPDetails(isin);
    return this.bpDetails$.asObservable();
  }

  private loadBPDetails(isin?: string): void {
    const httpClient: HttpClient = this.#http;
    this.initDone.then(() => {
      let bpDetailsUrl = `${environment.apihost}/api/trading/bpdetails/?customerId=${this.getSelectedCustomer()}`;
      if (isin) {
        bpDetailsUrl += `&isin=${isin}`;
      }
      firstValueFrom(httpClient.get<BuyingPowerDetails>(bpDetailsUrl))
        .then(
          (res) => {
            // Success
            this.bpDetails = res;
            this.bpDetails$.next(res);
          },
          () => {
            // Error
            this.bpDetails$.next(undefined); // calling .error() on a subject would terminate the subject ultimately
          },
        )
        .catch(() => {
          this.bpDetails$.next(undefined); // calling .error() on a subject would terminate the subject ultimately
        });
    });
  }

  public loadMoneyTransactions$(size?: number, direction?: 'asc' | 'desc'): Subject<MoneyTransactionsDataHolder> {
    this.moneyTransactionsReload$.next({ size, direction });
    return this.moneyTransactions$;
  }

  private reloadMoneyTransactions(size?: number, direction?: 'asc' | 'desc'): void {
    const httpClient: HttpClient = this.#http;
    this.initDone.then(() => {
      const moneyTxUrl = `${environment.apihost}/api/moneytransaction/?customerId=${this.getSelectedCustomer()}`;
      let params = new HttpParams();
      if (size) {
        params = params.append('size', `${size}`);
      }
      if (direction) {
        params = params.append('sort', `bookingDate,${direction}`);
      }
      firstValueFrom(httpClient.get(moneyTxUrl, { params }))
        .then(
          (res) => {
            // Success
            const moneyTransactionsDataHolder: MoneyTransactionsDataHolder = {
              list: [],
              hasNext: false,
            };
            Object.assign(moneyTransactionsDataHolder, res);
            for (let i = 0; i < moneyTransactionsDataHolder.list.length; i++) {
              moneyTransactionsDataHolder.list[i] = Object.assign(new MoneyTransaction(), moneyTransactionsDataHolder.list[i]);
            }
            this.moneyTransactions = moneyTransactionsDataHolder.list;
            this.hasMoreMoneyTransactions = moneyTransactionsDataHolder.hasNext;
            this.moneyTransactions$.next(moneyTransactionsDataHolder);
          },
          () => {
            // Error
            this.moneyTransactions$.next(undefined); // calling .error() on a subject would terminate the subject ultimately
          },
        )
        .catch(() => {
          this.moneyTransactions$.next(undefined); // calling .error() on a subject would terminate the subject ultimately
        });
    });
  }

  private resetAllData(): void {
    this.customerData = {};
    this.selectedCustomer = undefined;
    this.unreadTickets = 0;
    this.bpDetails = undefined;
    this.bpDetails$.next(undefined);
    this.moneyTransactions = [];
    this.moneyTransactions$.next({ list: [] });
    this.unreadDocuments = { eingang: 0, mifid: 0 };
    this.documents = new Map<string, Post[]>();
    this.adAgreeNotePostponed = undefined;
    this.enrolledDevices$.next(null);
  }

  private initCheckForNewClientVersion(): void {
    if (!environment.checkNewClientVersion) {
      return;
    }
    interval(300000).subscribe(() => {
      this.#http.get('/version.info', { responseType: 'text' }).subscribe((versionOnServer) => {
        if (!this.clientVersion) {
          this.clientVersion = versionOnServer;
        }
        if (this.clientVersion !== versionOnServer) {
          this.clientUpdateAvailable = true;
        }
      });
    });
  }

  public isNewClientVersionAvailable(): boolean {
    return this.clientUpdateAvailable;
  }

  private reloadUnreadTicketsCount(): void {
    if (!this.getSelectedCustomer()) {
      return;
    }

    const unreadTicketsCountBeforeReload = this.unreadTickets;
    this.#http
      .get<number>(`${environment.apihost + environment.ticketUrl}/${this.getSelectedCustomer()}/unread`)
      .pipe(catchError(() => of(0)))
      .subscribe((res) => {
        this.unreadTickets = res;
        if (!this.unreadTickets || unreadTicketsCountBeforeReload !== this.unreadTickets) {
          this.unreadTicketsCountChanged$.next();
        }
      });
  }

  public triggerReloadUnreadTickets(): void {
    this.unreadTicketsReloadTrigger$.next();
  }

  public toggleAdAgree(forcedState?: boolean): Observable<boolean> {
    const body = typeof forcedState !== 'undefined' ? { forcedState: forcedState } : null;
    return this.#http.post<boolean>(`${environment.apihost + environment.toggleAdAgreeUrl}/${this.selectedCustomer}`, body);
  }

  public postponeAdAgreeDecision(): void {
    this.adAgreeNotePostponed = new Date();
  }

  public weMayShowAdAgreeNote(): boolean {
    return !this.adAgreeNotePostponed || this.adAgreeNotePostponed.getTime() < new Date().getTime() - 1000 * 60 * 60;
  }

  public deleteCachedDocuments(): void {
    this.documents = new Map<string, Post[]>();
  }

  public getEnrolledDevices$(): Observable<EnrolledDevice[]> {
    if (!this.enrolledDevices$.value) {
      // not loaded yet - reload
      this.triggerReloadEnrolledDevices();
    }
    return this.enrolledDevices$.asObservable();
  }

  public triggerReloadEnrolledDevices(): void {
    this.enrolledDevicesReload$.next();
  }

  private reloadEnrolledDevices(): void {
    this.#http.get<EnrolledDevice[]>(`${environment.apihost + environment.appAuthUrl}/enrolleddevices`).subscribe((devices) => {
      this.enrolledDevices$.next(devices);
    });
  }

  public isAppAuthenticationEnabled(): Observable<boolean> {
    return HttpResponseHandleHelper.handleResponse(
      this.#http.get<boolean>(`${environment.apihost + environment.appAuthUrl}/appauthenabled`, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    ).pipe(map((result) => result.success && result.data));
  }

  public spacify(str: string): string {
    if (!str) {
      return '';
    }
    const after = 4;
    const c = ' ';
    const v = str.replace(/[^\dA-Z]/g, '');
    const reg = new RegExp(`.{${after}}`, 'g');
    return v.replace(reg, (a) => a + c).replace(/\D+$/, '');
  }

  private isPushMsgToBeStored(serverPushMsg: ServerPushMessage) {
    return serverPushMsg.type !== ServerPushMessageType.TSLUPDATE;
  }
}
